import type { Location } from 'react-router-dom';

/**
 * Where a signed-out visitor was going when the guard sent them to the login page.
 *
 * It travels in history state, not in the URL: a page reload keeps it, nothing outside
 * this origin can write it, and a link cannot plant one. A query parameter would also put
 * a path into every login URL, in front of a WAF that has already refused this app's own
 * parameters for looking like a file path (`profile*`, `session_id`).
 *
 * Validated on every read all the same, because history state is only as trustworthy as
 * the last script that touched it.
 */
type SignInReturnTargetState = { signInReturnTo: string };

const PARSE_BASE = 'https://sign-in-return-target.invalid';

/**
 * A same-origin relative path, in the form the browser itself would resolve, or `null`.
 *
 * Parsed rather than pattern-matched, so the browser's own rules decide: it reads `\` as
 * `/` and strips tabs and newlines, which turns `/\evil.example` and `/<tab>/evil.example`
 * into another host, and it folds `/..//evil.example` into a path starting with `//`.
 *
 * `/auth/*` and `/verification` are refused because those flows have entry rules of their
 * own, which the guard enforces. Sending somebody there afterwards can only fight them.
 */
const normalizeTarget = (target: unknown) => {
  if (typeof target !== 'string' || !target.startsWith('/')) return null;
  let url: URL;
  try {
    url = new URL(target, PARSE_BASE);
  } catch {
    return null;
  }
  if (url.origin !== PARSE_BASE || url.pathname.startsWith('//')) return null;
  // `startsWith` without a trailing slash, the same way the guard reads these prefixes.
  if (
    url.pathname.startsWith('/auth') ||
    url.pathname.startsWith('/verification')
  ) {
    return null;
  }
  const normalized = `${url.pathname}${url.search}${url.hash}`;
  return normalized === '/' ? null : normalized;
};

/** The state that carries this address to the login page, if it is worth returning to. */
export const signInReturnTargetFrom = ({
  hash,
  pathname,
  search,
}: Pick<Location, 'hash' | 'pathname' | 'search'>):
  | SignInReturnTargetState
  | undefined => {
  const signInReturnTo = normalizeTarget(`${pathname}${search}${hash}`);
  return signInReturnTo ? { signInReturnTo } : undefined;
};

/**
 * The state that passes a valid target on to the next redirect, and nothing else. Reading
 * the target goes through here too, so there is one validated answer per location.
 */
export const carrySignInReturnTarget = (
  state: unknown,
): SignInReturnTargetState | undefined => {
  const signInReturnTo =
    typeof state === 'object' && state !== null && 'signInReturnTo' in state
      ? normalizeTarget(state.signInReturnTo)
      : null;
  return signInReturnTo ? { signInReturnTo } : undefined;
};
